import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Flag, AlertTriangle, Ban, Trash2, Eye, RefreshCw, Check, X } from "lucide-react";
import { API, useAuth } from "@/App";

const STATUS_COLORS = {
  pending: "bg-yellow-500 text-black",
  reviewing: "bg-blue-500 text-white",
  resolved: "bg-green-600 text-white",
  dismissed: "bg-gray-600 text-white",
};

export default function AdminReports() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({ by_status: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [typeFilter, setTypeFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 25;

  const refresh = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (statusFilter) qs.set("status", statusFilter);
    if (typeFilter) qs.set("target_type", typeFilter);
    qs.set("limit", String(PAGE_SIZE));
    qs.set("skip", String(page * PAGE_SIZE));
    const [listRes, statsRes] = await Promise.all([
      fetch(`${API}/admin/reports?${qs.toString()}`, { credentials: "include" }),
      fetch(`${API}/admin/reports/stats`, { credentials: "include" }),
    ]);
    if (listRes.ok) {
      const d = await listRes.json();
      setReports(d.reports || []);
      setTotal(d.total || 0);
    }
    if (statsRes.ok) setStats(await statsRes.json());
    setLoading(false);
  }, [statusFilter, typeFilter, page]);

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/admin/me`, { credentials: "include" })
      .then(r => r.ok ? r.json() : { is_admin: false })
      .then(d => {
        setAuthorized(d.is_admin === true);
        if (d.is_admin) refresh();
        else setLoading(false);
      });
  }, [user, refresh]);

  if (!user) {
    navigate("/login");
    return null;
  }

  if (authorized === false) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
        <div className="bg-red-600 border-4 border-black p-8 rounded-lg max-w-md text-center shadow-[6px_6px_0_#fff]">
          <h1 className="text-3xl font-black mb-2">⛔ Admin Only</h1>
          <p className="mb-4">Your email isn't on the admin list.</p>
          <Button onClick={() => navigate("/")} className="mt-2 bg-white text-black border-2 border-black">
            Back home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0014] to-[#000] text-white py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-4xl font-black flex items-center gap-2">
            <Shield className="text-pink-400" /> Report Inbox
          </h1>
          <div className="flex gap-2">
            <Button onClick={() => navigate("/admin/contests")} variant="outline" className="border-2 border-white">
              Contest Admin
            </Button>
            <Button onClick={refresh} className="bg-yellow-400 text-black font-black border-2 border-black" data-testid="admin-reports-refresh">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard label="Total" value={stats.total} color="bg-white/10" />
          <StatCard label="Pending" value={stats.by_status.pending || 0} color="bg-yellow-500/20 border-yellow-500" />
          <StatCard label="Reviewing" value={stats.by_status.reviewing || 0} color="bg-blue-500/20 border-blue-500" />
          <StatCard label="Resolved" value={stats.by_status.resolved || 0} color="bg-green-600/20 border-green-600" />
          <StatCard label="Dismissed" value={stats.by_status.dismissed || 0} color="bg-gray-600/20 border-gray-600" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-5 items-center">
          <span className="text-xs uppercase text-pink-300 font-black">Status:</span>
          {["", "pending", "reviewing", "resolved", "dismissed"].map(s => (
            <button
              key={s || "all"}
              onClick={() => { setStatusFilter(s); setPage(0); }}
              className={`text-xs font-bold px-3 py-1 rounded border-2 border-black ${statusFilter === s ? "bg-pink-500" : "bg-white/5"}`}
              data-testid={`filter-status-${s || "all"}`}
            >
              {s || "all"}
            </button>
          ))}
          <span className="text-xs uppercase text-pink-300 font-black ml-4">Type:</span>
          {["", "user", "video", "post", "track", "message", "live"].map(t => (
            <button
              key={t || "all"}
              onClick={() => { setTypeFilter(t); setPage(0); }}
              className={`text-xs font-bold px-3 py-1 rounded border-2 border-black ${typeFilter === t ? "bg-cyan-500 text-black" : "bg-white/5"}`}
              data-testid={`filter-type-${t || "all"}`}
            >
              {t || "all"}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-pink-200">Loading reports…</p>
        ) : reports.length === 0 ? (
          <div className="border-2 border-dashed border-pink-500/50 rounded-lg p-12 text-center">
            <Flag className="w-12 h-12 text-pink-400 mx-auto mb-3" />
            <p className="text-pink-200">No reports match the current filters.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map(r => (
              <ReportRow key={r.report_id} report={r} onOpen={() => setSelected(r)} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > PAGE_SIZE ? (
          <div className="flex items-center justify-between mt-6 text-sm" data-testid="pagination-bar">
            <p className="text-pink-300">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total} (filtered) · {stats.total || 0} total reports
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                className="bg-white/10 border-2 border-black font-black"
                data-testid="page-prev"
              >
                ← Prev
              </Button>
              <Button
                size="sm"
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage(p => p + 1)}
                className="bg-white/10 border-2 border-black font-black"
                data-testid="page-next"
              >
                Next →
              </Button>
            </div>
          </div>
        ) : (
          !loading && reports.length > 0 && (
            <p className="text-xs text-pink-400 mt-4" data-testid="filter-summary">
              Showing {reports.length} of {total} (filtered) · {stats.total || 0} total reports across all statuses
            </p>
          )
        )}

        {selected && (
          <ReportDetailModal
            report={selected}
            onClose={() => setSelected(null)}
            onDone={() => { setSelected(null); refresh(); }}
          />
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className={`border-2 border-black rounded-lg p-4 ${color}`}>
      <p className="text-xs uppercase font-bold text-white/70">{label}</p>
      <p className="text-3xl font-black">{value}</p>
    </div>
  );
}

function ReportRow({ report, onOpen }) {
  const snap = report.target_snapshot || {};
  const label = snap.name || snap.title || snap.content || report.target_id || "—";
  return (
    <div
      className="bg-white/5 border-2 border-pink-500/30 rounded-lg p-4 flex flex-wrap items-center gap-4"
      data-testid={`report-row-${report.report_id}`}
    >
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className={`text-[10px] font-black px-2 py-0.5 rounded uppercase ${STATUS_COLORS[report.status] || "bg-gray-500"}`}>
            {report.status}
          </span>
          <span className="text-[10px] bg-cyan-400 text-black px-2 py-0.5 font-black rounded uppercase">
            {report.target_type || "user"}
          </span>
          <span className="text-[10px] bg-red-500 text-white px-2 py-0.5 font-black rounded uppercase">
            {report.reason}
          </span>
          {snap.is_banned && (
            <span className="text-[10px] bg-black text-red-400 border border-red-500 px-2 py-0.5 font-black rounded uppercase">BANNED</span>
          )}
        </div>
        <p className="font-bold text-sm truncate">Target: {label}</p>
        <p className="text-xs text-pink-200 truncate">
          Reporter: {report.reporter_name} · {new Date(report.created_at).toLocaleString()}
        </p>
        {report.details && (
          <p className="text-xs text-gray-400 mt-1 line-clamp-2">"{report.details}"</p>
        )}
      </div>
      <Button size="sm" onClick={onOpen} className="bg-yellow-400 text-black font-black border-2 border-black" data-testid={`open-report-${report.report_id}`}>
        <Eye className="w-3 h-3 mr-1" /> Review
      </Button>
    </div>
  );
}

function ReportDetailModal({ report, onClose, onDone }) {
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [suspendDays, setSuspendDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API}/admin/reports/${report.report_id}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(setDetail);
  }, [report.report_id]);

  const take = async (action) => {
    if (submitting) return;
    if (action === "ban" && !window.confirm("Permanently BAN this user? They won't be able to log in again.")) return;
    if (action === "delete_content" && !window.confirm("Delete this content? This cannot be undone.")) return;
    setSubmitting(true);
    const body = { action, reason: note };
    if (action === "suspend") body.suspend_days = Number(suspendDays) || 7;
    const res = await fetch(`${API}/admin/reports/${report.report_id}/action`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    setSubmitting(false);
    if (!res.ok) {
      toast.error(typeof data.detail === "string" ? data.detail : "Action failed");
      return;
    }
    toast.success(`Done: ${action}`);
    onDone();
  };

  const snap = detail?.target_snapshot || report.target_snapshot || {};

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-auto" onClick={onClose}>
      <div
        className="bg-[#1a0030] border-4 border-pink-500 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto p-6 shadow-[8px_8px_0_#ff00ff]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-black">Report #{report.report_id}</h2>
            <p className="text-xs text-pink-200">{new Date(report.created_at).toLocaleString()}</p>
          </div>
          <Button size="sm" variant="outline" onClick={onClose} className="border-2 border-white">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
          <Field label="Status" value={report.status} />
          <Field label="Target Type" value={report.target_type || "user"} />
          <Field label="Reason" value={report.reason} />
          <Field label="Target ID" value={report.target_id || report.reported_user_id} mono />
        </div>

        {report.details && (
          <div className="mb-4 bg-black/40 border-2 border-pink-500/40 rounded p-3">
            <p className="text-[10px] uppercase font-bold text-pink-300 mb-1">Details from reporter</p>
            <p className="text-sm">{report.details}</p>
          </div>
        )}

        {/* Target snapshot */}
        {Object.keys(snap).length > 0 && (
          <div className="mb-4 bg-white/5 border-2 border-cyan-500/40 rounded p-3">
            <p className="text-[10px] uppercase font-bold text-cyan-300 mb-1">Reported Content Snapshot</p>
            <pre className="text-[11px] whitespace-pre-wrap break-all text-gray-200">{JSON.stringify(snap, null, 2)}</pre>
          </div>
        )}

        {detail?.reporter && (
          <div className="mb-4 text-xs text-pink-200">
            Reporter: <b>{detail.reporter.name}</b> ({detail.reporter.email})
            {detail.related_count > 0 && (
              <span className="ml-2 bg-red-500 text-white px-2 py-0.5 rounded font-black">
                ⚠️ {detail.related_count} other reports on this target
              </span>
            )}
          </div>
        )}

        {/* Action log */}
        {(detail?.action_log || []).length > 0 && (
          <div className="mb-4 bg-black/40 rounded p-3 text-xs space-y-1">
            <p className="text-[10px] uppercase font-bold text-yellow-300">Action Log</p>
            {detail.action_log.map((a) => (
              <div key={`${a.at}-${a.action}`} className="text-gray-300">
                · <b>{a.action}</b> by {a.admin_email} at {new Date(a.at).toLocaleString()}
                {a.note && <span className="text-gray-400"> — {a.note}</span>}
              </div>
            ))}
          </div>
        )}

        {/* Admin note */}
        <div className="mb-4">
          <Label className="text-xs uppercase font-bold text-pink-300">Moderator note (optional)</Label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Reason shown to the user / recorded in log"
            className="bg-black border-2 border-pink-500/50 mt-1"
            data-testid="admin-report-note"
          />
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <Button disabled={submitting} onClick={() => take("mark_reviewing")} className="bg-blue-500 hover:bg-blue-400 border-2 border-black font-black" data-testid="action-reviewing">
            <Eye className="w-4 h-4 mr-1" /> Mark Reviewing
          </Button>
          <Button disabled={submitting} onClick={() => take("dismiss")} className="bg-gray-500 hover:bg-gray-400 border-2 border-black font-black" data-testid="action-dismiss">
            <Check className="w-4 h-4 mr-1" /> Dismiss
          </Button>
          <Button disabled={submitting} onClick={() => take("warn")} className="bg-yellow-500 hover:bg-yellow-400 text-black border-2 border-black font-black" data-testid="action-warn">
            <AlertTriangle className="w-4 h-4 mr-1" /> Warn User
          </Button>
          {["video", "post", "track"].includes(report.target_type) && (
            <Button disabled={submitting} onClick={() => take("delete_content")} className="bg-orange-600 hover:bg-orange-500 border-2 border-black font-black" data-testid="action-delete-content">
              <Trash2 className="w-4 h-4 mr-1" /> Delete Content
            </Button>
          )}
          <div className="flex items-center gap-1 col-span-2">
            <Input
              type="number"
              min={1}
              max={365}
              value={suspendDays}
              onChange={(e) => setSuspendDays(e.target.value)}
              className="w-20 bg-black border-2 border-pink-500/50"
              data-testid="suspend-days"
            />
            <Button disabled={submitting} onClick={() => take("suspend")} className="bg-purple-600 hover:bg-purple-500 border-2 border-black font-black flex-1" data-testid="action-suspend">
              Suspend {suspendDays}d
            </Button>
          </div>
          <Button disabled={submitting} onClick={() => take("ban")} className="bg-red-600 hover:bg-red-500 border-2 border-black font-black col-span-2 md:col-span-1" data-testid="action-ban">
            <Ban className="w-4 h-4 mr-1" /> BAN USER
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <p className="text-[10px] uppercase font-bold text-pink-300">{label}</p>
      <p className={`text-sm ${mono ? "font-mono text-xs break-all" : ""}`}>{value || "—"}</p>
    </div>
  );
}
